/**
 * Role-aware label mapping for order delivery + settlement state machines.
 *
 * Backend states are kept generic (single source of truth). The UI translates
 * them per role so buyers and suppliers see their own next-action mental model
 * instead of internal operations wording.
 */

// ─── Delivery state machine (order.status) ──────────────────────────────────
const DELIVERY_BUYER = {
  pending_payment: "Awaiting invoice",
  payment_received: "Payment sent",
  processing: "Order in progress",
  shipped: "In transit",
  delivered: "Delivered",
  closed: "Closed",
  disputed: "Disputed",
};

const DELIVERY_SUPPLIER = {
  pending_payment: "Issue invoice",
  payment_received: "Payment received",
  processing: "Order assembly",
  shipped: "In transit",
  delivered: "Delivered",
  closed: "Closed",
  disputed: "Disputed",
};

const DELIVERY_NEUTRAL = {
  pending_payment: "Pending payment",
  payment_received: "Payment received",
  processing: "Processing",
  shipped: "Shipped",
  delivered: "Delivered",
  closed: "Closed",
  disputed: "Disputed",
};

// ─── Settlement state machine (order.settlement.status) ─────────────────────
const SETTLEMENT_BUYER = {
  awaiting_invoice: "Awaiting supplier invoice",
  invoiced: "Invoice received — pay supplier off-platform",
  buyer_confirmed_paid: "Payment sent — awaiting confirmation",
  settled: "Payment confirmed",
  disputed: "Disputed",
};

const SETTLEMENT_SUPPLIER = {
  awaiting_invoice: "Issue invoice",
  invoiced: "Awaiting buyer payment",
  buyer_confirmed_paid: "Buyer marked payment sent — confirm receipt",
  settled: "Payment received",
  disputed: "Disputed",
};

const SETTLEMENT_NEUTRAL = {
  awaiting_invoice: "Awaiting invoice",
  invoiced: "Invoiced",
  buyer_confirmed_paid: "Buyer marked paid",
  settled: "Settled",
  disputed: "Disputed",
};

// ─── Action button labels (transition: next state) ──────────────────────────
const ACTION_BUYER = {
  payment_received: "Mark payment sent",
  closed: "Confirm receipt",
  disputed: "Open dispute",
};

const ACTION_SUPPLIER = {
  processing: "Confirm payment received",
  shipped: "Add tracking & mark shipped",
  delivered: "Mark delivered",
  disputed: "Open dispute",
};

// ─── Public helpers ─────────────────────────────────────────────────────────
export function deliveryLabel(status, role) {
  if (!status) return "";
  if (role === "buyer") return DELIVERY_BUYER[status] || prettify(status);
  if (role === "supplier") return DELIVERY_SUPPLIER[status] || prettify(status);
  return DELIVERY_NEUTRAL[status] || prettify(status);
}

export function settlementLabel(status, role) {
  if (!status) return "";
  if (role === "buyer") return SETTLEMENT_BUYER[status] || prettify(status);
  if (role === "supplier") return SETTLEMENT_SUPPLIER[status] || prettify(status);
  return SETTLEMENT_NEUTRAL[status] || prettify(status);
}

/** Action button label for transitioning to `nextStatus`. Returns null if the
 *  given role shouldn't see this transition (UI hides the button). */
export function deliveryActionLabel(nextStatus, role) {
  if (role === "buyer") return ACTION_BUYER[nextStatus] || null;
  if (role === "supplier") return ACTION_SUPPLIER[nextStatus] || null;
  return prettify(nextStatus);
}

function prettify(s) {
  return String(s).replace(/_/g, " ");
}
